<template>
  <div class="list-proposals">
    <AppPageHeader title="Proposals Dashboard" />

    <OpportunityGraph />

    <div class="card mt-8 overflow-x-auto">
      <BaseTable
        :is-loading="isPending"
        :error="error"
        :fields="tableFields"
        :items="proposals || []"
        :is-searchable="true"
        :paginate="true"
        :next-page="nextPage"
        :prev-page="prevPage"
        :page="currentPage"
        :total-records="totalRecords"
        :total-pages="totalPages"
        :number-per-page="numPerPage"
        class="mt-8 min-w-[800px]"
        @on-search="(val) => search(val)"
      >
        <template #status-data="data">
          <BaseBadge v-if="data.row.status === PROPOSAL_STATUS.DECLINED" variant="error" class="capitalize whitespace-nowrap">
            {{ capitalize(formatSnakeCase(data.row.status)) }}
          </BaseBadge>
          <BaseBadge v-else-if="data.row.status === PROPOSAL_STATUS.ACCEPTED" variant="success" class="capitalize whitespace-nowrap">
            {{ capitalize(formatSnakeCase(data.row.status)) }}
          </BaseBadge>
          <BaseBadge v-else-if="data.row.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE" variant="warning" class="capitalize whitespace-nowrap">
            {{ capitalize('waiting') }}
          </BaseBadge>
        </template>
        <template #contractDate-data="data">
          <span class="whitespace-nowrap">{{ formatDate(data.row.contractDate) }}</span>
        </template>
        <template #projectId-data="data">
          <RouterLink v-if="projects?.find(project => project.id === data.row.projectId)?.id" :to="{ name: 'ShowProposal', params: { id: data.row.projectId } }" class="hover:underline">
            <span class="truncate max-w-[200px] inline-block">
              {{ projects?.find(project => project.id === data.row.projectId)?.name || data.row.projectId }}
            </span>
          </RouterLink>
          <span v-else class="truncate max-w-[200px] inline-block">
            {{ data.row.projectId }}
          </span>
        </template>
        <template #customerId-data="data">
          <RouterLink v-if="customers?.find(customer => customer.id === data.row.customerId)?.id" :to="{ name: 'ShowCustomer', params: { id: data.row.customerId} }" class="hover:underline">
            <span class="truncate max-w-[200px] inline-block">
              {{ customers?.find(customer => customer.id === data.row.customerId)?.name || data.row.customerId }}
            </span>
          </RouterLink>
          <span v-else class="truncate max-w-[200px] inline-block">
            {{ data.row.customerId }}
          </span>
        </template>
        <template #contractTotal-data="data">
          {{ data.row.contractTotal > 0 ? currency(data.row.contractTotal) : '--' }}
        </template>
        <template #approvedChangeOrderTotal-data="data">
          {{ (data.row.approvedChangeOrderTotal || 0) > 0 ? currency(data.row.approvedChangeOrderTotal) : '--' }}
        </template>
        <template #overallTotal-data="data">
          <span :title="`Original: ${currency(data.row.contractTotal || 0)}\nApproved CO: ${currency(data.row.approvedChangeOrderTotal || 0)}`">
            {{ (data.row.contractTotal + (data.row.approvedChangeOrderTotal || 0)) > 0 ? currency(data.row.contractTotal + (data.row.approvedChangeOrderTotal || 0)) : '--' }}
          </span>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import { formatSnakeCase, capitalize, formatDate } from '@/utils'
import { currency } from '@/utils'
import { useHead } from '@unhead/vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import OpportunityGraph from '@/components/proposal/OpportunityGraph.vue'
import { useQuery } from '@tanstack/vue-query'
import { inArray } from '@/utils/queryFilters'
import ProposalService from '@/services/ProposalService'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import { useSearchablePagination } from '@/composables/useSearchablePagination'

useHead({ title: 'Proposals Dashboard' })

const tableFields = [
  { key: 'status', label: 'Status', sortable: true },
  { key: 'contractDate', label: 'Contract Date', sortable: true },
  { key: 'projectId', label: 'Project', sortable: true },
  { key: 'customerId', label: 'Customer', sortable: true },
  { key: 'contractTotal', label: 'Original Contract', sortable: true },
  { key: 'approvedChangeOrderTotal', label: 'Approved CO Value', sortable: true },
  { key: 'overallTotal', label: 'Overall Total', sortable: true },
]

const numPerPage = ref(25)
const proposalService = new ProposalService()

const {
  results: proposals,
  isPending,
  error,
  search,
  currentPage,
  totalPages,
  totalRecords,
  nextPage,
  prevPage,
} = useSearchablePagination<Proposal>({
  indexName: 'proposals',
  service: proposalService,
  numPerPage: numPerPage.value,
  defaultOrderBy: 'contractDate',
  defaultOrderDir: 'desc',
  baseQuery: [inArray('status', [PROPOSAL_STATUS.ACCEPTED, PROPOSAL_STATUS.AWAITING_ACCEPTANCE, PROPOSAL_STATUS.DECLINED])]
})


const uniqueProjects = computed(() => Array.from(new Set(proposals.value?.map(proposal => proposal.projectId))))
const projectService = new ProjectService()
const { data: projects } = useQuery({
  queryKey: ['projects', uniqueProjects, currentPage],
  queryFn: () => projectService.where([inArray('id', uniqueProjects.value as string[])], [])
})

const uniqueCustomers = computed(() => Array.from(new Set(proposals.value?.map(proposal => proposal.customerId))))
const customerService = new CustomerService()
const { data: customers } = useQuery({
  queryKey: ['customers', uniqueCustomers, currentPage],
  queryFn: () => customerService.where([inArray('id', uniqueCustomers.value as string[])], [])
})
</script>
